import { Injectable } from '@angular/core';
import { BaseAppService } from './configurators-2d/base-app-service';
import { BasketConfigurationModel } from '../models/basket-configuration-model';
import { CONFIG_CONTEXT } from '../enum/config-context.enum';
import { environment } from 'src/environments/environment';
import { ProcessQuoteService } from './process-quote.service';
import { logError } from './helpers/sentry.helper';
import { PurchaseListItem } from '../models/purchase-list-item';
import { BasketProductMapper } from './helpers/basket-product-mapper';
import { PurchaseListStore } from '../stores/purchase-list.store';
import { ProductListStore } from '../stores/product-list/product-list.store';
import { GlobalResetService } from './global-reset.service';
import { ConfigurationHistory } from '../models/basket/configuration-history';
import { BASKET_DEFAULT_THUMBNAIL } from '../consts/basket/basket-thumbnail.consts';

@Injectable({
  providedIn: 'root',
})
export class BasketService extends BaseAppService<BasketConfigurationModel> {
  constructor(
    private readonly processQuoteService: ProcessQuoteService,
    private readonly purchaseListStore: PurchaseListStore,
    private readonly productListStore: ProductListStore,
    private readonly globalResetService: GlobalResetService
  ) {
    super(CONFIG_CONTEXT.BASKET, BasketConfigurationModel);
    this.globalResetService.register(() => this.resetConfigurationAndPurchaseList());
  }

  isWaitingForOpportunityNumber: boolean = false;
  opportunityNumber: number | null = null;
  pendingSend: boolean = false;

  /**
   * Est-ce que le devis a été envoyé ?
   */
  quoteSend: boolean = false;

  public async saveProjectAndGetOpportunityNumber(): Promise<number> {
    try {
      this.isWaitingForOpportunityNumber = true;
      this.addBasketProductsToConfiguration(this.purchaseListStore.products());
      this.configuration.discountPercentage = this.purchaseListStore.discountPercentage();
      await this.saveProject();
      const res = await this.generateXML(this.configuration.id);
      this.opportunityNumber = await this.getOpportunityNumber(res.xml);
      await this.updateOrderAndSaveProject(res.orderId, this.opportunityNumber);
      this.isWaitingForOpportunityNumber = false;
      return this.opportunityNumber;
    } catch (err) {
      logError(err);
      this.isWaitingForOpportunityNumber = false;
      return -1;
    }
  }

  override saveProject(): Promise<boolean> {
    this.addBasketProductsToConfiguration(this.purchaseListStore.products());
    this.configuration.discountPercentage = this.purchaseListStore.discountPercentage();
    this.setDefaultThumbnailIfMissing();
    return super.saveProject();
  }

  /**
   * Attache la miniature par défaut à la configuration.
   * Le back supprime la miniature à chaque mise à jour : elle doit être renvoyée à chaque
   * enregistrement, faute de quoi le projet devient introuvable dans la liste des projets.
   */
  private setDefaultThumbnailIfMissing(): void {
    if (this.configuration.thumbnail?.data) {
      return;
    }

    this.configuration.thumbnail = { data: BASKET_DEFAULT_THUMBNAIL };
  }

  private async getOpportunityNumber(xml: string): Promise<number> {
    if (environment.production) {
      try {
        return await this.processQuoteService.sendDotComPlusOrder(xml);
      } catch (err) {
        logError(err);
        throw new Error(
          "La génération du devis (Numéro d'opportunité) à échoué, veuillez contacter un administrateur."
        );
      }
    } else {
      const blob = new Blob([xml], { type: 'text/xml' });
      const url = globalThis.URL.createObjectURL(blob);

      return new Promise<number>(
        resolve =>
          setTimeout(() => {
            globalThis.open(url);
            resolve(Math.floor(Math.random() * 1_000_000));
          }, 2000) // Génération d'un entier aléatoire entre 0 et 999999)
      );
    }
  }

  addBasketProductsToConfiguration(purchaseList: PurchaseListItem[]): void {
    this.configuration.basketProducts = BasketProductMapper.toBasketProducts(purchaseList);
  }

  /**
   * Charge la configuration panier demandée et réalimente la liste d'achat avec son contenu.
   * @param configurationId identifiant de la configuration à rouvrir
   */
  async loadPurchaseListFromConfiguration(configurationId: string): Promise<void> {
    const configuration = await this.getProjectById(configurationId, true);
    if (!configuration) {
      return;
    }

    this.purchaseListStore.restore(BasketProductMapper.toPurchaseListItems(configuration.basketProducts ?? []));
    this.purchaseListStore.setDiscountPercentage(configuration.discountPercentage ?? null);
  }

  /** Réinitialise la configuration et la liste d'achat pour démarrer un nouveau panier. */
  startNewPurchaseList(): void {
    this.reset();
    this.purchaseListStore.reset();
  }

  resetConfigurationAndPurchaseList() {
    this.reset();
    this.purchaseListStore.reset();
    this.productListStore.reset();
    this.customer.logout();
  }

  /**
   * Récupère l'historique des ventes de la journée du vendeur connectée
   */
  getVendorHistory(): Promise<ConfigurationHistory[]> {
    return this.get<ConfigurationHistory[]>(`${this.apiUrl}configuration/VendorHistory`);
  }

  async sendToMail(): Promise<void> {
    await this.post(`${this.apiUrl}configuration/SendToMail`, {
      configurationId: this.configuration.id,
      email: this.customer.userDatas.email
    });
  }
}
